import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { sendContactMessage } from "../lib/api";

const EMPTY = { name: "", email: "", phone: "", message: "" };

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await sendContactMessage({
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: "Website Chat Enquiry",
        message: form.message,
      });
      setSent(true);
      setForm(EMPTY);
      toast.success("Message sent", { description: res?.message || "We'll be in touch soon." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Please try again or email info@smibhaluxecandles.com.";
      toast.error("Couldn't send your message", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const field = (label, key, type = "text", extra = {}) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-zinc-500 block mb-1.5">
        {label}
      </span>
      {type === "textarea" ? (
        <textarea
          rows={3}
          value={form[key]}
          onChange={update(key)}
          className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 resize-none"
          data-testid={`chat-${key}`}
          {...extra}
        />
      ) : (
        <input
          type={type}
          value={form[key]}
          onChange={update(key)}
          className="w-full border border-zinc-300 px-3 h-10 text-sm focus:outline-none focus:border-zinc-900"
          data-testid={`chat-${key}`}
          {...extra}
        />
      )}
    </label>
  );

  return (
    <>
      {/* Floating launcher button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open contact chat"}
        data-testid="floating-chat-btn"
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 w-14 h-14 rounded-full luxe-bg text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? "x" : "chat"}
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {open ? <X size={24} strokeWidth={2} /> : <MessageCircle size={24} strokeWidth={2} />}
          </motion.span>
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            data-testid="chat-panel"
            className="fixed z-[60] bottom-24 right-5 left-5 sm:left-auto sm:right-6 sm:bottom-24 sm:w-[370px] max-h-[75vh] overflow-y-auto bg-[#FBF8F3] border border-zinc-200 shadow-2xl rounded-lg"
          >
            {/* Header */}
            <div className="sticky top-0 luxe-bg text-white px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">Smibha Luxe</div>
                <div className="text-lg font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
                  Contact Us
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="chat-close-btn"
                className="p-1.5 rounded-full hover:bg-white/15 transition"
              >
                <X size={20} strokeWidth={2} />
              </button>
            </div>

            <div className="p-5">
              {sent ? (
                <div className="text-center py-6" data-testid="chat-success">
                  <CheckCircle2 size={40} className="mx-auto mb-4 text-emerald-500" strokeWidth={1.5} />
                  <div className="font-black text-lg tracking-tight" style={{ fontFamily: "Outfit" }}>
                    Message sent
                  </div>
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                    Thank you — our team will reply within one business day.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-4 text-[11px] uppercase tracking-[0.25em] font-bold underline"
                    data-testid="chat-send-another"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="chat-form">
                  <p className="text-xs text-zinc-500 leading-relaxed -mt-1">
                    Questions about a candle or your order? Send us a note.
                  </p>
                  {field("Name", "name", "text", { required: true })}
                  {field("Email", "email", "email", { required: true })}
                  {field("Phone", "phone", "tel")}
                  {field("Comment", "message", "textarea", { required: true, minLength: 10 })}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="chat-send-btn"
                    className="w-full bg-zinc-900 text-white px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:bg-black transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Sending
                      </>
                    ) : (
                      <>
                        <Send size={14} /> Send
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
